/**
 * Shared option sets for the Custom SQL metric: the aggregation methods and
 * result units the form offers. Centralized so the config form, the Sofia prompt,
 * and the Sofia-reply parser all agree on the allowed values.
 */

import { pickUnitOptions, type UnitOption } from "./unitOptions"

/**
 * Aggregation methods the SQL Float metric supports. Lower-case values match the
 * `aggregation_method` metadata field; the backend upper-cases them when
 * parsing, so these map onto the backend AggregationMethod enum
 * (SUM/AVERAGE/MIN/MAX/COUNT).
 */
export const SQL_AGGREGATION_METHOD_OPTIONS = [
  { key: "average", label: "Average" },
  { key: "sum", label: "Sum" },
  { key: "min", label: "Minimum" },
  { key: "max", label: "Maximum" },
  { key: "count", label: "Count" },
] as const

export type SqlAggregationMethod =
  (typeof SQL_AGGREGATION_METHOD_OPTIONS)[number]["key"]

const SQL_AGGREGATION_METHOD_KEYS: readonly string[] =
  SQL_AGGREGATION_METHOD_OPTIONS.map((option) => option.key)

export function isSqlAggregationMethod(
  value: string | undefined,
): value is SqlAggregationMethod {
  return value !== undefined && SQL_AGGREGATION_METHOD_KEYS.includes(value)
}

/**
 * Output units the SQL Float metric can report. `metadataUnit` is written to
 * `metric_metadata.unit` and must be a value of the backend `ResultUnit` enum --
 * the backend validates it strictly and rejects anything else, so this is a
 * curated, numeric-appropriate subset of the shared unit catalog rather than
 * free text. "Unitless" stores no unit (the backend default).
 */
export const SQL_UNIT_OPTIONS = pickUnitOptions([
  "none",
  "s",
  "ms",
  "count",
  "ratio",
  "percent",
  "per_minute",
  "per_second",
  "per_message",
  "tokens",
  "score",
  "severity",
  "quality",
  "dB",
  "LUFS",
  "Hz",
  "st",
])

// Accept the canonical keys plus the natural-language labels Sofia might use.
const AGGREGATION_ALIASES: Record<string, SqlAggregationMethod> = {
  average: "average",
  avg: "average",
  mean: "average",
  sum: "sum",
  total: "sum",
  min: "min",
  minimum: "min",
  max: "max",
  maximum: "max",
  count: "count",
}

/** Maps a free-text aggregation token (key or label) to a canonical method. */
export function resolveAggregationMethod(
  token: string,
): SqlAggregationMethod | null {
  return AGGREGATION_ALIASES[token.trim().toLowerCase()] ?? null
}

/**
 * Maps a free-text unit token to a unit option. Returns `{ metadataUnit }` where
 * `metadataUnit` is undefined for the unitless option, or null if unrecognized.
 */
export function resolveUnitToken(
  token: string,
): { metadataUnit: UnitOption["metadataUnit"] } | null {
  const normalized = token.trim().toLowerCase()
  if (normalized.length === 0) {
    return null
  }
  if (normalized === "none" || normalized === "unitless") {
    return { metadataUnit: undefined }
  }
  const option = SQL_UNIT_OPTIONS.find(
    (candidate) =>
      candidate.key.toLowerCase() === normalized ||
      candidate.metadataUnit?.toLowerCase() === normalized ||
      candidate.label.toLowerCase() === normalized,
  )
  return option ? { metadataUnit: option.metadataUnit } : null
}
